type PredictionLike = {
  predictionDate: string;
};

const roundToTwoDecimals = (value: number): number => Math.round(value * 100) / 100;

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const toPredictionTimestamp = (predictionDate: string): number => {
  const timestamp = new Date(predictionDate).getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
};

export const sortPredictionsByDate = <T extends PredictionLike>(
  predictionList: T[] | undefined,
): T[] => {
  if (!Array.isArray(predictionList)) {
    return [];
  }

  return [...predictionList].sort((a, b) => {
    const aTime = toPredictionTimestamp(a.predictionDate);
    const bTime = toPredictionTimestamp(b.predictionDate);
    return aTime - bTime;
  });
};

export const ensureNonIncreasingSeries = (series: number[]): number[] => {
  const normalized = series.map((value) => roundToTwoDecimals(clamp(value, 0, 100)));
  for (let index = 1; index < normalized.length; index++) {
    if (normalized[index] > normalized[index - 1]) {
      normalized[index] = normalized[index - 1];
    }
  }
  return normalized;
};

export const buildHazardRateSeries = (
  healthSeries: Array<number | null | undefined>,
): Array<number | null> => {
  if (!Array.isArray(healthSeries) || healthSeries.length === 0) {
    return [];
  }

  const normalized = healthSeries.map((value) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return null;
    }
    return roundToTwoDecimals(clamp(value, 0, 100));
  });

  return normalized.map((currentValue, index) => {
    const nextValue = normalized[index + 1];

    if (currentValue === null || currentValue <= 0) {
      return null;
    }
    if (nextValue === null || nextValue === undefined) {
      return null;
    }

    const adjustedNextValue = Math.max(0, Math.min(nextValue, currentValue));
    const hazardRate = ((currentValue - adjustedNextValue) / currentValue) * 100;
    return roundToTwoDecimals(clamp(hazardRate, 0, 100));
  });
};
